import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import DesigneeSearchA from 'components/DesigneeSearch/DesigneeSearchA';
import { loader } from 'graphql.macro';
import { sortBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';

const DESIGNEE_SEARCH_QUERY = loader('./designeeSearch.graphql');

const DesigneeSearchContainer = (props) => {
  const {
    firstName: firstNameOnProps,
    lastName: lastNameOnProps,
    city: cityOnProps,
    zipCode: zipCodeOnProps,
    selectedState: selectedStateOnProps,
    selectedDesignation: selectedDesignationOnProps,
    stateList: stateListOnProps,
    designationList: designationListOnProps,
    shouldTriggerSearchOnLoad: shouldTriggerSearchOnLoadOnProps,
  } = props;
  const [firstName, setFirstName] = useState(firstNameOnProps);
  const [lastName, setLastName] = useState(lastNameOnProps);
  const [city, setCity] = useState(cityOnProps);
  const [zipCode, setZipCode] = useState(zipCodeOnProps);
  const [selectedState, setSelectedState] = useState(selectedStateOnProps);
  const [selectedDesignation, setSelectedDesignation] = useState(selectedDesignationOnProps);
  const [stateList] = useState(stateListOnProps);
  const [designationList] = useState(sortBy(designationListOnProps, ['acronym', 'name']));
  const [shouldTriggerSearchOnLoad] = useState(shouldTriggerSearchOnLoadOnProps);
  const [activePage, setActivePage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ field: 'lastName', order: 0 });
  const [emptySearchError, setEmptySearchError] = useState('');

  const onChangeFirstName = useCallback((val) => setFirstName(val), [setFirstName]);
  const onChangeLastName = useCallback((val) => setLastName(val), [setLastName]);
  const onChangeCity = useCallback((val) => setCity(val), [setCity]);
  const onChangeSelectedState = useCallback((val) => setSelectedState(val), [setSelectedState]);
  const onChangeZipCode = useCallback((val) => setZipCode(val), [setZipCode]);
  const onChangeSelectedDesignation = useCallback((val) => setSelectedDesignation(val), [setSelectedDesignation]);
  const onClear = useCallback(() => {
    setFirstName('');
    setLastName('');
    setCity('');
    setSelectedState(null);
    setZipCode('');
    setSelectedDesignation(null);
  }, [setFirstName, setLastName, setCity, setSelectedState, setZipCode, setSelectedDesignation]);

  const [performSearch, { data, error, loading }] = useLazyQuery(DESIGNEE_SEARCH_QUERY);

  const updateQueryStrings = () => {
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('city');
      searchParams.delete('designation');
      searchParams.delete('firstName');
      searchParams.delete('lastName');
      searchParams.delete('zipCode');
      searchParams.delete('state');
      if (city) searchParams.set('city', city);
      if (selectedDesignation) searchParams.set('designation', selectedDesignation.acronym);
      if (firstName) searchParams.set('firstName', firstName);
      if (lastName) searchParams.set('lastName', lastName);
      if (zipCode) searchParams.set('zipCode', zipCode);
      if (selectedState) searchParams.set('state', selectedState.code);
      const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState(null, '', newRelativePathQuery);
    }
  };

  const onSubmit = (evt) => {
    evt.preventDefault();
    if (firstName || lastName || city || zipCode || selectedState || selectedDesignation) {
      updateQueryStrings();
      performSearch({
        variables: {
          acronym: selectedDesignation ? selectedDesignation.acronym : '',
          firstName,
          lastName,
          city,
          state: selectedState ? selectedState.code : '',
          zipCode,
          orderBy: 'lastName',
          sortMethod: 0,
          page: 0,
          pageSize: 10,
        },
      });
      setActivePage(1);
      setEmptySearchError('');
    } else {
      setEmptySearchError('Please enter at least one search criteria.');
    }
  };

  const onPageChange = (page) => {
    if (page !== activePage) {
      performSearch({
        variables: {
          acronym: selectedDesignation ? selectedDesignation.acronym : '',
          firstName,
          lastName,
          city,
          state: selectedState ? selectedState.code : '',
          zipCode,
          orderBy: sortConfig.field,
          sortMethod: sortConfig.order,
          page: page - 1,
          pageSize: 10,
        },
      });
      setActivePage(page);
    }
  };

  const onSortChange = ({ field, order }) => {
    const { field: fieldInState, order: orderInState } = sortConfig;
    if (field !== fieldInState || order !== orderInState) {
      setSortConfig({ field, order });
      performSearch({
        variables: {
          acronym: selectedDesignation ? selectedDesignation.acronym : '',
          firstName,
          lastName,
          city,
          state: selectedState ? selectedState.code : '',
          zipCode,
          orderBy: field,
          sortMethod: order,
          page: 0,
          pageSize: 10,
        },
      });
      setActivePage(1);
    }
  };

  useEffect(() => {
    if (shouldTriggerSearchOnLoad) {
      performSearch({
        variables: {
          acronym: selectedDesignation ? selectedDesignation.acronym : '',
          firstName,
          lastName,
          city,
          state: selectedState ? selectedState.code : '',
          zipCode,
          orderBy: sortConfig.field,
          sortMethod: sortConfig.order,
          page: 0,
          pageSize: 10,
        },
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DesigneeSearchA
      results={data}
      error={(error && error.message) || emptySearchError}
      onSubmit={onSubmit}
      firstName={firstName}
      lastName={lastName}
      city={city}
      selectedState={selectedState}
      zipCode={zipCode}
      selectedDesignation={selectedDesignation}
      stateList={stateList}
      designationList={designationList}
      onChangeFirstName={onChangeFirstName}
      onChangeLastName={onChangeLastName}
      onChangeCity={onChangeCity}
      onChangeSelectedState={onChangeSelectedState}
      onChangeZipCode={onChangeZipCode}
      onChangeSelectedDesignation={onChangeSelectedDesignation}
      onClear={onClear}
      activePage={activePage}
      onPageChange={onPageChange}
      sortConfig={sortConfig}
      onSortChange={onSortChange}
      loading={loading}
    />
  );
};

DesigneeSearchContainer.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  city: PropTypes.string,
  zipCode: PropTypes.string,
  selectedState: PropTypes.object,
  selectedDesignation: PropTypes.object,
  stateList: PropTypes.array,
  designationList: PropTypes.array,
  shouldTriggerSearchOnLoad: PropTypes.bool,
};

export default DesigneeSearchContainer;
